/**
 * <countdown-timer> — live countdown used by the announcement bar.
 *
 * Markup contract (see blocks/_announcement.liquid):
 *   <countdown-timer data-end="2030-12-31T23:59:59">
 *     <div data-countdown-timer>
 *       <span data-days></span><span data-hours></span>
 *       <span data-minutes></span><span data-seconds></span>
 *     </div>
 *     <div data-countdown-expired>…</div>
 *   </countdown-timer>
 *
 * Two modes:
 *   1. Fixed end date — data-end="2030-12-31T23:59:59" (parsed as local time;
 *      a bare space between date and time is normalised to `T` for Safari).
 *   2. Rolling duration — data-duration="7200" (seconds from page load). The
 *      computed end is stored in localStorage under data-key so it keeps
 *      counting across reloads instead of restarting on every refresh; a fresh
 *      window starts once the previous one has elapsed.
 */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.endDate = this.resolveEndDate();

    this.clockEl = this.querySelector('[data-countdown-timer]');
    this.expiredEl = this.querySelector('[data-countdown-expired]');
    this.daysEl = this.querySelector('[data-days]');
    this.hoursEl = this.querySelector('[data-hours]');
    this.minutesEl = this.querySelector('[data-minutes]');
    this.secondsEl = this.querySelector('[data-seconds]');

    // Bad/empty date → leave the initial server-rendered values in place.
    if (isNaN(this.endDate.getTime())) return;

    this.update = this.update.bind(this);
    this.update();
    this.interval = setInterval(this.update, 1000);
  }

  disconnectedCallback() {
    if (this.interval) clearInterval(this.interval);
  }

  resolveEndDate() {
    const duration = parseInt(this.getAttribute('data-duration'), 10);

    // Rolling "time from page load" mode.
    if (duration > 0) {
      const key = 'countdown-timer-' + (this.getAttribute('data-key') || 'default');
      let end;
      try {
        end = parseInt(localStorage.getItem(key), 10);
      } catch (e) {
        end = NaN;
      }
      // No active window (never started or already elapsed) → start a fresh one.
      if (isNaN(end) || end - Date.now() <= 0) {
        end = Date.now() + duration * 1000;
        try {
          localStorage.setItem(key, String(end));
        } catch (e) {
          /* storage unavailable (private mode) — falls back to per-load timing */
        }
      }
      return new Date(end);
    }

    // Fixed end-date mode.
    const raw = (this.getAttribute('data-end') || '').trim().replace(' ', 'T');
    return new Date(raw);
  }

  update() {
    const diff = this.endDate.getTime() - Date.now();

    if (diff <= 0) {
      if (this.clockEl) this.clockEl.style.display = 'none';
      if (this.expiredEl) this.expiredEl.style.display = 'block';
      if (this.interval) clearInterval(this.interval);
      return;
    }

    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (this.daysEl) this.daysEl.textContent = days;
    if (this.hoursEl) this.hoursEl.textContent = hours;
    if (this.minutesEl) this.minutesEl.textContent = minutes;
    if (this.secondsEl) this.secondsEl.textContent = seconds;
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
